import React, {useState, useEffect} from 'react'
import {useTranslation} from 'react-i18next'

import {Box, Grid, TextField} from '@mui/material'
import {
  PGRadioBtnGroup,
  PGButton,
  PGModal,
  PGTypography,
} from '@progynyglobal/pg-ui-library'

import MobileStickyActions, {
  MOBILE_STICKY_HEIGHT,
} from '@progyny/components/MobileStickyActions'
import {MenopauseSymtoms, UNSET} from '@progyny/const/index'
import {useProductLine} from '@progyny/hooks/useProductLine'
import {useResponsive} from '@progyny/hooks/useResponsive'
import MenopauseSymptomsModal from '@progyny/pages/SetUpProfile/MenopauseSymptoms/MenopauseSymptomsModal'
import {SetupProfileSubProps} from '@progyny/pages/SetUpProfile/types'

const MenopauseSymptoms: React.FC<SetupProfileSubProps> = (props) => {
  const {callback, onboardingData, persistMemberData, navigateTo, match} = props

  const {t} = useTranslation()
  const {productLine} = useProductLine()
  const isMobile = useResponsive('down', 'md')
  const [selectedValues, setSelectedValues] = useState<string[]>([])
  const [otherSymptom, setSetOtherSymptom] = useState<string>('')
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)

  useEffect(() => {
    if (
      onboardingData.menopauseSymptoms &&
      Array.isArray(onboardingData.menopauseSymptoms)
    ) {
      const validSymptoms = onboardingData.menopauseSymptoms.filter(
        (symptom): symptom is string =>
          symptom !== null && symptom !== undefined
      )
      setSelectedValues(validSymptoms)
    }
    if (onboardingData.otherSymptomsDescription) {
      setSetOtherSymptom(onboardingData.otherSymptomsDescription)
    }
  }, [
    onboardingData.menopauseSymptoms,
    onboardingData.otherSymptomsDescription,
  ])

  const choices = MenopauseSymtoms.filter((item) => {
    if (item.value === UNSET) return false
    return true
  }).map((item) => ({
    value: item.value,
    label: t(item?.label!) || '',
  }))

  const handleSymptomChange = (
    value: string | number | (string | number)[]
  ) => {
    const newValues = value as string[]
    const currentlyHasNone = selectedValues.includes('none')
    const newHasNone = newValues.includes('none')

    // Special handling when "none" is currently selected
    if (currentlyHasNone) {
      if (newHasNone) {
        const valuesWithoutNone = newValues.filter((v) => v !== 'none')
        if (valuesWithoutNone.length > 0) {
          setSelectedValues(valuesWithoutNone)
        } else {
          setSelectedValues([])
        }
      } else {
        setSelectedValues(newValues)
      }
    } else if (newHasNone && !currentlyHasNone) {
      setSelectedValues(['none'])
    } else {
      setSelectedValues(newValues)
    }
  }

  const handleChoiceClick = () => {
    const finalOtherSymptom = selectedValues.includes('other')
      ? otherSymptom
      : ''

    persistMemberData(
      {
        menopauseSymptoms: selectedValues,
        otherSymptomsDescription: finalOtherSymptom,
      },
      callback
    )
  }

  const handleBackClick = () => {
    if (onboardingData.menstrualPeriodStatus === 'no') {
      navigateTo(1, match.url)
    } else {
      navigateTo(0, match.url)
    }
  }

  const handleLearnMoreClick = () => {
    setIsModalOpen(true)
  }

  return (
    <>
      <Box
        display={'flex'}
        flexDirection={'column'}
        alignItems={'center'}
        mb={3}
      >
        <PGTypography variant={'body'} textAlign={'center'}>
          {t(
            'set_up_profile_menopause_symptoms_knowing_your_symptoms_helps_us'
          )}{' '}
          <PGTypography
            component='span'
            variant='body'
            sx={{
              color: 'primary.main',
              cursor: 'pointer',
              textDecoration: 'underline',
              '&:hover': {
                textDecoration: 'none',
              },
            }}
            onClick={handleLearnMoreClick}
          >
            {t('common_learn_more')}
          </PGTypography>
        </PGTypography>
      </Box>

      <Grid
        container
        justifyContent={'center'}
        sx={{pb: {xs: MOBILE_STICKY_HEIGHT, md: 0}}}
      >
        <Grid item xs={12}>
          <Box
            mt={3}
            width='100%'
            display={'flex'}
            flexDirection={'column'}
            gap={3}
          >
            <PGRadioBtnGroup
              options={choices}
              values={selectedValues}
              productLine={
                productLine as
                  | 'fertility'
                  | 'pregnancy_and_postpartum'
                  | 'menopause'
              }
              columns={isMobile ? 1 : 2}
              content='checkmark'
              multiSelect={true}
              onChange={handleSymptomChange}
            />
            {selectedValues?.includes('other') && (
              <TextField
                value={otherSymptom}
                onChange={(e) => setSetOtherSymptom(e.target.value)}
                label={t('setup_profile_menopause_symptoms_please_specify')}
                variant='outlined'
                fullWidth
              />
            )}
          </Box>
        </Grid>
      </Grid>
      <MobileStickyActions desktopMt={6}>
        <Box display={'flex'} justifyContent={'space-between'} gap={2}>
          <PGButton
            label={t('common_back')}
            onClick={handleBackClick}
            variant='outlined'
          />
          <PGButton
            variant='contained'
            label={t('common_continue')}
            onClick={handleChoiceClick}
            disabled={
              selectedValues.length === 0 ||
              (selectedValues.includes('other') && !otherSymptom.trim())
            }
          />
        </Box>
      </MobileStickyActions>
      <PGModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={t('setup_profile_menopause_symptoms_learn_more_title')}
        maxWidth='md'
        minWidth='unset'
        sx={{
          '& .MuiDialog-paper': {
            margin: {xs: '8px', md: '32px'},
            width: {xs: 'calc(100% - 16px)', md: 'auto'},
          },
          '& .MuiDialogTitle-root': {
            paddingTop: '24px',
            paddingBottom: 0,
            paddingLeft: {xs: '16px', md: '24px'},
            paddingRight: {xs: '16px', md: '56px'},
          },
          '& .MuiDialogTitle-root .MuiBox-root': {
            top: {xs: '8px', md: '16px'},
            right: {xs: '8px', md: '16px'},
          },
        }}
      >
        <MenopauseSymptomsModal />
      </PGModal>
    </>
  )
}

export default MenopauseSymptoms
